/**
 * Review Repository
 * Extends BaseRepository for product and shop reviews
 */

import { ProductReview, ShopReview } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    getDocs,
    doc as firestoreDoc,
    updateDoc,
    addDoc,
    QueryDocumentSnapshot,
    DocumentData
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';

export interface ReviewEntity extends BaseEntity {
    productId?: string;
    shopId?: string;
    userId: string;
    userName: string;
    rating: number;
    comment: string;
    status: 'pending' | 'approved' | 'rejected';
    partnerResponse?: string;
    respondedAt?: string;
    helpful: number;
    notHelpful: number;
    type: 'product' | 'shop';
}

export interface ReviewVoteEntity extends BaseEntity {
    reviewId: string;
    userId: string;
    voteType: 'helpful' | 'not_helpful';
}

class ReviewRepository extends BaseRepository<ReviewEntity> {
    protected collectionName = COLLECTIONS.REVIEWS;
    private shopReviewsColl = COLLECTIONS.SHOP_REVIEWS;
    private votesColl = 'reviewVotes';

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): ReviewEntity {
        const data = doc.data();
        return {
            id: doc.id,
            productId: data.productId as string | undefined,
            shopId: data.shopId as string | undefined,
            userId: (data.userId as string) || '',
            userName: (data.userName as string) || '',
            rating: (data.rating as number) || 5,
            comment: (data.comment as string) || '',
            status: (data.status as ReviewEntity['status']) || 'pending',
            partnerResponse: data.partnerResponse as string | undefined,
            respondedAt: data.respondedAt as string | undefined,
            helpful: (data.helpful as number) || 0,
            notHelpful: (data.notHelpful as number) || 0,
            type: (data.type as ReviewEntity['type']) || 'product'
        };
    }

    async getProductReviews(productId: string, limitCount: number = 50): Promise<ProductReview[]> {
        try {
            const q = query(
                this.collection,
                where('productId', '==', productId),
                where('type', '==', 'product'),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as ProductReview))
                .filter(r => r.status === 'approved');
        } catch (error) {
            logger.error(`[ReviewRepository] Error fetching reviews for product ${productId}`, error);
            return [];
        }
    }

    async getShopReviews(shopId: string, limitCount: number = 50): Promise<ShopReview[]> {
        try {
            const q = query(
                collection(db, this.shopReviewsColl),
                where('shopId', '==', shopId),
                where('type', '==', 'shop'),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as ShopReview))
                .filter(r => r.status === 'approved');
        } catch (error) {
            logger.error(`[ReviewRepository] Error fetching reviews for shop ${shopId}`, error);
            return [];
        }
    }

  async getPendingProductReviews(): Promise<ProductReview[]> {
    try {
      // Simplified query to avoid composite index requirement
      // Just order by createdAt and filter client-side
      const q = query(
        this.collection,
        orderBy('createdAt', 'desc'),
        firestoreLimit(200)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ProductReview))
        .filter(r => r.status === 'pending');
    } catch (error) {
      logger.error('[ReviewRepository] Error fetching pending product reviews', error);
      return [];
    }
  }

  async getPendingShopReviews(): Promise<ShopReview[]> {
    try {
      // Simplified query to avoid composite index requirement
      const q = query(
        collection(db, this.shopReviewsColl),
        orderBy('createdAt', 'desc'),
        firestoreLimit(200)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ShopReview))
        .filter(r => r.status === 'pending');
    } catch (error) {
      logger.error('[ReviewRepository] Error fetching pending shop reviews', error);
      return [];
    }
  }

  async getProductReviewsByStatus(status: 'pending' | 'approved' | 'rejected'): Promise<ProductReview[]> {
    try {
      const q = query(
        this.collection,
        orderBy('createdAt', 'desc'),
        firestoreLimit(200)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ProductReview))
        .filter(r => r.status === status);
    } catch (error) {
      logger.error(`[ReviewRepository] Error fetching ${status} product reviews`, error);
      return [];
    }
  }

  async getShopReviewsByStatus(status: 'pending' | 'approved' | 'rejected'): Promise<ShopReview[]> {
    try {
      const q = query(
        collection(db, this.shopReviewsColl),
        orderBy('createdAt', 'desc'),
        firestoreLimit(200)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ShopReview))
        .filter(r => r.status === status);
    } catch (error) {
      logger.error(`[ReviewRepository] Error fetching ${status} shop reviews`, error);
      return [];
    }
  }

    async createProductReview(review: Omit<ProductReview, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
        const docRef = await addDoc(collection(db, this.collectionName), {
            ...review,
            type: 'product',
            status: 'pending',
            helpful: 0,
            notHelpful: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        if (review.productId) {
            CacheInvalidator.invalidateProduct(review.productId);
        }
        return docRef.id;
    }

    async createShopReview(review: Omit<ShopReview, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
        const docRef = await addDoc(collection(db, this.shopReviewsColl), {
            ...review,
            type: 'shop',
            status: 'pending',
            helpful: 0,
            notHelpful: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        if (review.shopId) {
            CacheInvalidator.invalidateShop(review.shopId);
        }
        return docRef.id;
    }

    async approveProductReview(reviewId: string): Promise<void> {
        try {
            const docRef = firestoreDoc(this.collection, reviewId);
            await updateDoc(docRef, { status: 'approved', approvedAt: new Date().toISOString() });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error approving product review ${reviewId}`, error);
            throw error;
        }
    }

    async approveShopReview(reviewId: string): Promise<void> {
        try {
            const docRef = firestoreDoc(collection(db, this.shopReviewsColl), reviewId);
            await updateDoc(docRef, { status: 'approved', approvedAt: new Date().toISOString() });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error approving shop review ${reviewId}`, error);
            throw error;
        }
    }

    async rejectProductReview(reviewId: string, reason?: string): Promise<void> {
        try {
            const docRef = firestoreDoc(this.collection, reviewId);
            await updateDoc(docRef, {
                status: 'rejected',
                rejectedAt: new Date().toISOString(),
                rejectionReason: reason || ''
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error rejecting product review ${reviewId}`, error);
            throw error;
        }
    }

    async rejectShopReview(reviewId: string, reason?: string): Promise<void> {
        try {
            const docRef = firestoreDoc(collection(db, this.shopReviewsColl), reviewId);
            await updateDoc(docRef, {
                status: 'rejected',
                rejectedAt: new Date().toISOString(),
                rejectionReason: reason || ''
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error rejecting shop review ${reviewId}`, error);
            throw error;
        }
    }

    async respondToProductReview(reviewId: string, response: string): Promise<void> {
        try {
            const docRef = firestoreDoc(this.collection, reviewId);
            await updateDoc(docRef, {
                partnerResponse: response,
                respondedAt: new Date().toISOString()
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error responding to product review ${reviewId}`, error);
            throw error;
        }
    }

    async respondToShopReview(reviewId: string, response: string): Promise<void> {
        try {
            const docRef = firestoreDoc(collection(db, this.shopReviewsColl), reviewId);
            await updateDoc(docRef, {
                partnerResponse: response,
                respondedAt: new Date().toISOString()
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[ReviewRepository] Error responding to shop review ${reviewId}`, error);
            throw error;
        }
    }
}

export const reviewRepository = new ReviewRepository();
